<script lang="ts" setup>import { toRef as _toRef, computed as _computed, ref as _ref } from 'vue';

import { faPlay, faPause } from '@fortawesome/pro-solid-svg-icons'
import { useRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import WaveSurfer from 'wavesurfer.js'

const __$temp_1 = (useRecipientLanding()),
  gift = _toRef(__$temp_1, 'gift'),
  senderName = _toRef(__$temp_1, 'senderName');

const voiceMemo = _computed(
  () => gift.value.greeting?.greetingCardCustomization?.voiceMemo,
)

let isPlaying = _ref(true)
let duration = _ref(0)
let currentTime = _ref(0)

const waveformRef = _ref<HTMLElement>()
let wavesurfer: WaveSurfer | null = null

const displayTime = _computed(() => {
  const t = currentTime.value > 0 ? currentTime.value : duration.value
  const hrs = Math.floor(t / 3600)
  const mins = Math.floor((t % 3600) / 60)
  const secs = Math.floor(t % 60)
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
})

function getAudioUrl (): string | null {
  if (!voiceMemo.value || typeof voiceMemo.value === 'string') return voiceMemo.value || null
  if (voiceMemo.value.signedId) {
    return `/rails/active_storage/blobs/proxy/${voiceMemo.value.signedId}/${voiceMemo.value.filename}`
  }
  return null
}

function initWavesurfer (url: string) {
  if (!waveformRef.value) return

  wavesurfer = WaveSurfer.create({
    container: waveformRef.value,
    waveColor: '#c8d1dc',
    progressColor: '#00a2f1', // primary-base
    cursorColor: 'transparent',
    cursorWidth: 0,
    barWidth: 3,
    barGap: 2,
    barRadius: 100,
    height: 62,
    autoplay: true,
    interact: true,
    dragToSeek: true,
  })

  wavesurfer.load(url)

  wavesurfer.on('ready', () => {
    duration.value = wavesurfer!.getDuration()
  })

  wavesurfer.on('timeupdate', (time) => {
    currentTime.value = time
    updateProgressOverlay()
  })

  wavesurfer.on('seeking', () => {
    updateProgressOverlay()
  })

  wavesurfer.on('finish', () => {
    isPlaying.value = false
    currentTime.value = 0
  })
}

let progressPercent = _ref(0)

function updateProgressOverlay () {
  if (!wavesurfer || !duration.value) return
  progressPercent.value = (wavesurfer.getCurrentTime() / duration.value) * 100
}

function togglePlay () {
  if (!wavesurfer) return

  if (isPlaying.value) {
    wavesurfer.pause()
  }
  else {
    wavesurfer.play()
  }
  isPlaying.value = !isPlaying.value
}

onMounted(async () => {
  const url = getAudioUrl()
  if (url) {
    await nextTick()
    initWavesurfer(url)
  }
})

onBeforeUnmount(() => {
  if (wavesurfer) {
    wavesurfer.destroy()
    wavesurfer = null
  }
})
</script>

<template>
  <div class="voice-memo-slide">
    <div class="voice-memo-header">
      <span class="voice-memo-label">Voice Memo from</span>
      <span class="voice-memo-sender">{{ senderName || 'Sender' }}</span>
    </div>

    <div class="voice-memo-time">
      {{ displayTime }}
    </div>

    <div class="voice-memo-waveform-wrapper">
      <div ref="waveformRef" class="voice-memo-waveform" @click.stop/>
      <div
        class="voice-memo-waveform-unplayed"
        :style="{ left: `${progressPercent}%` }"
      />
      <div
        class="voice-memo-cursor"
        :style="{ left: `${progressPercent}%` }"
      />
    </div>

    <GIconButton
      class="play-btn"
      variant="primary"
      :aria-label="isPlaying ? 'Pause voice memo' : 'Play voice memo'"
      :icon="isPlaying ? faPause : faPlay"
      size="large"
      @click.stop="togglePlay"
    />
  </div>
</template>

<style scoped lang="scss">
.voice-memo-slide {
  align-items: center;
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-evenly;
  padding: 24px 20px;
  width: 100%;
}

.voice-memo-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: center;
}

.voice-memo-label {
  color: var(--color-grey-500, #94a3b8);
  font-size: 14px;
}

.voice-memo-sender {
  @apply text-black;

  font-size: 16px;
  font-weight: 700;
}

.voice-memo-time {
  @apply text-navy;

  font-size: 24px;
  font-weight: 600;
  letter-spacing: 2px;
  margin-top: 20px;
}

.voice-memo-waveform-wrapper {
  flex-shrink: 0;
  height: 64px;
  margin: 40px 0;
  position: relative;
  width: 100%;
}

.voice-memo-waveform {
  height: 100%;
  width: 100%;
}

.voice-memo-waveform-unplayed {
  background: rgb(255 255 255 / 60%);
  bottom: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
}

.voice-memo-cursor {
  background: #00a2f1;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgb(0 162 241 / 25%);
  height: 10px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  z-index: 100;
}

.play-btn {
  border-radius: 14px;
  height: 45px;
  width: 45px;
}
</style>
